import { memo, useMemo, useState, useEffect } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MatchCard } from "./MatchCard";
import { Button } from "./Button";
// import { BannerAd } from "./BannerAd";
import { Users, Sparkles, Award, Loader2, Activity } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useMatches } from "../hooks/useMatches";
import { useAuth } from "../contexts/AuthContext";
import { UserMetrics } from "./UserMetrics";
import { useAppStats, formatNumber } from "../hooks/useAppStats";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { useSquad } from "../contexts/SquadContext";
import { getDateKey, formatDateHeader } from "../lib/dateUtils";
import type { Match, Screen } from "../types";

interface LandingScreenProps {
  onMatchClick: (match: Match) => void;
  onNavigate: (screen: Exclude<Screen, "predictor">) => void;
}

export const LandingScreen = memo(function LandingScreen({
  onMatchClick,
  onNavigate,
}: LandingScreenProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { currentSquad } = useSquad();
  const {
    matches,
    matchesWithPredictions,
    loading: matchesLoading,
  } = useMatches(user?.id);
  const { stats } = useAppStats();
  const { leaderboard } = useLeaderboard(1000);

  // Get league-specific user stats from leaderboard
  const userStats = useMemo(() => {
    if (!user || !leaderboard.length) return null;
    const userEntry = leaderboard.find((entry) => entry.user_id === user.id);
    if (!userEntry) return null;
    return {
      totalPredictions: userEntry.total_predictions || 0,
      correctPredictions: userEntry.correct_predictions || 0,
    };
  }, [user, leaderboard]);

  // Group matches by date
  const groupedMatches = useMemo(() => {
    const groups: Record<string, Match[]> = {};

    matches.forEach((match) => {
      const dateKey = getDateKey(match.matchTime);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(match);
    });

    // Sort date keys
    const sortedDates = Object.keys(groups).sort();

    return sortedDates.map((dateKey) => ({
      dateKey,
      dateHeader: formatDateHeader(dateKey, t),
      matches: groups[dateKey],
    }));
  }, [matches, t]);

  // Fake online users count - changes periodically to feel dynamic
  const [onlineUsers, setOnlineUsers] = useState(() => {
    // Start with a random number between 15-45
    return Math.floor(Math.random() * 30) + 15;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      // Change the count by ±3-8 users every 8-15 seconds
      const change = Math.floor(Math.random() * 6) + 3;
      const direction = Math.random() > 0.5 ? 1 : -1;
      setOnlineUsers((prev) => {
        const newCount = prev + change * direction;
        // Keep it between 10 and 60
        return Math.max(10, Math.min(60, newCount));
      });
    }, Math.random() * 7000 + 8000); // Random interval between 8-15 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header onNavigate={onNavigate} showNav={true} />

      <main className="flex-1 max-w-md mx-auto w-full px-4 py-6">
        {/* Hero Section */}
        <div className="gradient-primary rounded-3xl p-7 mb-6 text-white relative overflow-hidden shadow-xl">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-yellow-400 p-2 rounded-xl">
                <Sparkles className="w-4 h-4 text-primary-dark" />
              </div>
              <span className="text-yellow-400 text-sm font-semibold">
                AFCON 2025
              </span>
            </div>
            <h1 className="mb-2">{t("landing.hero.title")}</h1>
            <p className="mb-5 opacity-90 text-white/90">
              {t("landing.hero.subtitle")}
            </p>

            <div className="flex gap-2.5">
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/20">
                <div className="text-yellow-400 text-2xl font-semibold mb-1">
                  {formatNumber(stats.totalSquads)}
                </div>
                <div className="text-xs opacity-90">
                  {t("landing.stats.leagues")}
                </div>
              </div>
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/20">
                <div className="text-yellow-400 text-2xl font-semibold mb-1">
                  {formatNumber(stats.totalPredictions)}
                </div>
                <div className="text-xs opacity-90">
                  {t("landing.stats.predictions")}
                </div>
              </div>
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/20">
                <div className="text-yellow-400 text-2xl font-semibold mb-1">
                  {formatNumber(stats.totalUsers)}
                </div>
                <div className="text-xs opacity-90">
                  {t("landing.stats.users")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <UserMetrics variant="card" />
          <div className="bg-white rounded-2xl p-5 hover-lift border border-slate-200">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-2 rounded-xl">
                <Award className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs text-slate-600 font-medium uppercase tracking-wide">
                {t("landing.stats.predictions")}
              </span>
            </div>
            <div className="text-primary text-2xl font-semibold">
              {userStats
                ? `${userStats.correctPredictions}/${userStats.totalPredictions}`
                : "--"}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {userStats && userStats.totalPredictions > 0
                ? `${Math.round(
                    (userStats.correctPredictions /
                      userStats.totalPredictions) *
                      100
                  )}% ${t("leaderboard.accuracy")}`
                : t("common.noPredictions")}
            </div>
          </div>
        </div>

        {/* Online Users Indicator */}
        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></div>
            <div className="relative bg-green-500 p-2 rounded-full">
              <Activity className="w-4 h-4 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-green-900">
              {onlineUsers} {t("landing.onlineUsers")}
            </div>
            <div className="text-xs text-green-700">
              {t("landing.onlineUsersSubtext")}
            </div>
          </div>
        </div>

        {/* CTA Button - Only show if user hasn't joined a squad */}
        {!currentSquad && (
          <Button
            variant="secondary"
            fullWidth
            onClick={() => onNavigate("leaderboard")}
            className="mb-6 shadow-md hover-lift"
          >
            <div className="flex items-center justify-center gap-2">
              <Users className="w-5 h-5" />
              <span>{t("landing.cta.createLeague")}</span>
            </div>
          </Button>
        )}

        {/* Banner Ad - Between CTA and Matches */}
        {/* <div className="mb-6">
          <BannerAd size="medium" variant="subtle" />
        </div> */}

        {/* Matches List */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-slate-900">{t("landing.matches.title")}</h2>
            <span className="text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full font-medium">
              {matches.length} {t("landing.stats.matches").toLowerCase()}
            </span>
          </div>
          {matchesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>{t("landing.noMatches")}</p>
            </div>
          ) : (
            groupedMatches.map((group) => (
              <div key={group.dateKey} className="mb-6">
                {/* Date Header */}
                <div className="flex items-center gap-2.5 mb-4">
                  <h3 className="text-slate-900 font-semibold text-lg">
                    {group.dateHeader}
                  </h3>
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                    {group.matches.length}{" "}
                    {group.matches.length === 1
                      ? t("match.singular")
                      : t("match.plural")}
                  </span>
                </div>

                {/* Matches for this date */}
                {group.matches.map((match) => (
                  <MatchCard
                    key={match.id}
                    {...match}
                    hasPrediction={matchesWithPredictions.has(match.id)}
                    onClick={() => onMatchClick(match)}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </main>

      <Footer onNavigate={onNavigate} />
    </div>
  );
});
